import { Injectable } from "@angular/core";
import { environment } from "environments/environment";
import { HttpClient } from "@angular/common/http";
import { BaseResponse } from "app/data/shared/models/base-response";
import { CurrencyDto } from "app/data/management/models/currency.dto";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

@Injectable()
export class ApiService {
  private baseUrl() {
    return (
      (localStorage.getItem("apiUrl")
        ? localStorage.getItem("apiUrl")
        : environment.apiUrl) + "/"
    );
  }

  constructor(private httpClient: HttpClient) {}

  public getFilterDataByDate(service: any, date?: any): Observable<any> {
    return this.httpClient
      .post<BaseResponse<CurrencyDto[]>>(
        `${this.baseUrl()}/${service}/${date}`,
        {}
      )
      .pipe(
        map((response) => response.data),
        catchError((err) => throwError(err))
      );
  }

  public getFilteredData(endpoint, filter, filterChild?) {
    return this.httpClient
      .post<BaseResponse<any>>(
        this.baseUrl() +
          endpoint +
          "/filter" +
          (filterChild ? "/" + filterChild : ""),
        filter
      )
      .pipe(
        map((response) => response),
        catchError((err) => throwError(err))
      );
  }

  public getSpecialFilteredData(endpoint, filter) {
    return this.httpClient
      .post<BaseResponse<any>>(this.baseUrl() + endpoint, filter)
      .pipe(
        map((response) => response),
        catchError((err) => throwError(err))
      );
  }

  public getFilteredDataByDate(endpoint, date) {
    endpoint = endpoint + "/" + date;
    return this.httpClient
      .post<BaseResponse<any>>(this.baseUrl() + endpoint, {})
      .pipe(
        map((response) => response.data),
        catchError((err) => throwError(err))
      );
  }

  public getDataById(endpoint, id): Observable<any> {
    endpoint = endpoint + "/" + id;
    return this.httpClient
      .get<BaseResponse<any>>(this.baseUrl() + endpoint)
      .pipe(
        map((response) => response.data),
        catchError((err) => throwError(err))
      );
  }

  public getDataByContainer(endpoint, id, payload: any): Observable<any> {
    endpoint = endpoint + "/" + id;
    return this.httpClient
      .post<BaseResponse<any>>(this.baseUrl() + endpoint, payload)
      .pipe(
        map((response) => response.data),
        catchError((err) => throwError(err))
      );
  }

  public postDataById(endpoint, id, payload): Observable<any> {
    endpoint = endpoint + "/" + id;
    return this.httpClient
      .post<BaseResponse<any>>(this.baseUrl() + endpoint, payload)
      .pipe(
        map((response) => response.data),
        catchError((err) => throwError(err))
      );
  }

  public getAllData(endpoint) {
    return this.httpClient
      .get<BaseResponse<any>>(this.baseUrl() + endpoint)
      .pipe(
        map((response) => response.data),
        catchError((err) => throwError(err))
      );
  }

  public getParamsOf(endpoint): Observable<any> {
    endpoint = endpoint + "/" + environment.filterableElementSelector;
    return this.httpClient
      .get<BaseResponse<any>>(this.baseUrl() + endpoint)
      .pipe(
        map((response) => response.data),
        catchError((err) => throwError(err))
      );
  }

  public addData(endpoint, payload): Observable<any> {
    return this.httpClient
      .post<BaseResponse<any>>(this.baseUrl() + endpoint, payload)
      .pipe(
        map((response) => response.data),
        catchError((err) => throwError(err))
      );
  }

  public updateData(endpoint, payload): Observable<any> {
    return this.httpClient
      .put<BaseResponse<any>>(this.baseUrl() + endpoint, payload)
      .pipe(
        map((response) => response.data),
        catchError((err) => throwError(err))
      );
  }

  public deleteData(endpoint, id): Observable<any> {
    endpoint = endpoint + "/" + id;
    return this.httpClient
      .delete<BaseResponse<any>>(this.baseUrl() + endpoint)
      .pipe(
        map((response) => response),
        catchError((err) => throwError(err))
      );
  }

  public uploadImage(endpoint, id, payload): Observable<any> {
    endpoint = endpoint + "/" + id + "/image";
    return this.httpClient
      .post<BaseResponse<any>>(this.baseUrl() + endpoint, payload)
      .pipe(
        map((response) => response.data),
        catchError((err) => throwError(err))
      );
  }

  public lockData(endpoint, id): Observable<any> {
    endpoint = endpoint + "/" + id + "/" + environment.entityLockSelector;
    return this.httpClient
      .get<BaseResponse<any>>(this.baseUrl() + endpoint)
      .pipe(
        map((response) => response.data),
        catchError((err) => throwError(err))
      );
  }

  public unlockData(endpoint, id): Observable<any> {
    endpoint = endpoint + "/" + id + "/" + environment.entityUnlockSelector;
    return this.httpClient
      .get<BaseResponse<any>>(this.baseUrl() + endpoint)
      .pipe(
        map((response) => response.data),
        catchError((err) => throwError(err))
      );
  }

  public generateCode(endpoint): Observable<any> {
    endpoint = endpoint + "/" + environment.generateCodeSelector;
    return this.httpClient
      .get<BaseResponse<any>>(this.baseUrl() + endpoint)
      .pipe(
        map((response) => response.data),
        catchError((err) => throwError(err))
      );
  }

  public getChanges(endpoint, id): Observable<any> {
    endpoint = endpoint + "/" + id + "/" + environment.changesSelector;
    return this.httpClient
      .get<BaseResponse<any>>(this.baseUrl() + endpoint)
      .pipe(
        map((response) => response.data),
        catchError((err) => throwError(err))
      );
  }

  public getCompany(endpoint, id): Observable<any> {
    endpoint = endpoint + "/" + id + "/" + "companies";
    return this.httpClient
      .get<BaseResponse<any>>(this.baseUrl() + endpoint)
      .pipe(
        map((response) => response.data),
        catchError((err) => throwError(err))
      );
  }

  public getLocationsByType(type: string): Observable<any> {
    const url = `${this.baseUrl()}location/type/${type.toUpperCase()}`;
    return this.httpClient.get<any>(url).pipe(
      map((response) => response.data),
      catchError((err) => throwError(err))
    );
  }

  public getLocationByParentId(parentId: number): Observable<any> {
    return this.httpClient
      .get<any>(`${this.baseUrl()}location/${parentId}/child`)
      .pipe(
        map((response) => response.data),
        catchError((err) => throwError(err))
      );
  }

  public getLocationById(Id: number): Observable<any> {
    return this.httpClient.get<any>(`${this.baseUrl()}location/${Id}`).pipe(
      map((response) => response.data),
      catchError((err) => throwError(err))
    );
  }

  public getLocationExist(parentId: number, exists: string): Observable<any> {
    return this.httpClient
      .get<any>(
        `${this.baseUrl()}location/${parentId}/child/${exists.toUpperCase()}/exists`
      )
      .pipe(
        map((response) => response.data),
        catchError((err) => throwError(err))
      );
  }

  public changePassword(payload: any): Observable<any> {
    return this.httpClient
      .post<any>(`${this.baseUrl()}password/change`, payload)
      .pipe(
        map((response) => response),
        catchError((err) => throwError(err))
      );
  }

  public getBranchOfCompany(id: any): Observable<any> {
    return this.httpClient
      .get<BaseResponse<any>>(`${this.baseUrl()}company/${id}/getbranch`)
      .pipe(
        map((response) => response.data),
        catchError((err) => throwError(err))
      );
  }
  public getHistory(endpoint, id, childEndpoint): Observable<any> {
    const url = `${this.baseUrl()}${endpoint}/${id}/${childEndpoint}`;
    return this.httpClient.get<BaseResponse<any>>(url).pipe(
      map((response) => response.data),
      catchError((err) => throwError(err))
    );
  }

  public postWithoutData(endpoint, payload): Observable<any> {
    return this.httpClient
      .post<BaseResponse<any>>(this.baseUrl() + endpoint, payload)
      .pipe(
        map((response) => response),
        catchError((err) => throwError(err))
      );
  }
}
